import { PropertyStatus, PropertyType } from "../types";
import { PropertyActions } from "./PropertyActions";
import { PropertyResponseDto } from "@/common/api/datasources/Api";
import { Badge } from "@/components/ui/badge";
import { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { Building2, Home } from "lucide-react";
import { Link } from "react-router-dom";

export const getPropertyColumns = (t: (key: string) => string): ColumnDef<PropertyResponseDto>[] => {
    return [
        {
            id: "property",
            header: t("properties.columns.property"),
            cell: ({ row }) => {
                const property = row.original;
                const icon = property.type === PropertyType.APARTMENT ? <Building2 className="w-8 h-8" /> : <Home className="w-8 h-8" />;

                return (
                    <Link
                        to={`/home/properties/${property.propertyUUID}`}
                        className="flex items-center gap-3 cursor-pointer hover:bg-muted/50 rounded-md p-1 transition-colors"
                    >
                        <div className="flex items-center justify-center w-24 h-24 rounded-md bg-muted">
                            {property.images && Array.isArray(property.images) && property.images.length > 0 && property.images[0]?.thumbnail?.url ? (
                                <img
                                    src={property.images[0].thumbnail.url}
                                    alt={property.name}
                                    className="w-full h-full object-cover rounded-md"
                                />
                            ) : (
                                icon
                            )}
                        </div>
                        <div className="flex flex-col">
                            <span className="font-medium">{property.name}</span>
                            <span className="text-sm text-muted-foreground">
                                {property.address || property.city || t("properties.noAddress")}
                            </span>
                        </div>
                    </Link>
                );
            },
        },
        {
            accessorKey: "type",
            header: () => t("properties.columns.type"),
            cell: ({ row }) => {
                const type = row.getValue("type") as PropertyType;
                if (!type) return "-";

                return (
                    <Badge variant="secondary">
                        {t(`properties.type.${type}`)}
                    </Badge>
                );
            },
        },
        {
            accessorKey: "status",
            header: () => t("properties.columns.status"),
            cell: ({ row }) => {
                const status = row.getValue("status") as PropertyStatus;
                if (!status) return "-";

                let variant: "default" | "secondary" | "destructive" | "outline" = "default";

                switch (status) {
                    case PropertyStatus.AVAILABLE:
                        variant = "default";
                        break;
                    case PropertyStatus.PENDING:
                        variant = "secondary";
                        break;
                    case PropertyStatus.MAINTENANCE:
                        variant = "outline";
                        break;
                    case PropertyStatus.SOLD:
                        variant = "destructive";
                        break;
                    case PropertyStatus.OCCUPIED:
                        variant = "secondary";
                        break;
                }

                return (
                    <Badge variant={variant}>
                        {t(`properties.status.${status}`)}
                    </Badge>
                );
            },
        },
        {
            accessorKey: "price",
            header: () => t("properties.columns.price"),
            cell: ({ row }) => {
                const price = row.getValue("price") as number;
                const currency = row.original.currency;

                if (!price || !currency) return "-";

                try {
                    return new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: currency,
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                    }).format(price);
                } catch (error) {
                    console.error('Error formatting price:', error);
                    return `${price} ${currency}`;
                }
            },
        },
        {
            accessorKey: "area",
            header: () => t("properties.columns.area"),
            cell: ({ row }) => {
                const area = row.getValue("area") as number;
                return area ? `${area} m²` : "-";
            },
        },
        {
            accessorKey: "rooms",
            header: () => t("properties.columns.rooms"),
            cell: ({ row }) => {
                const rooms = row.getValue("rooms") as number;
                return rooms || "-";
            },
        },
        {
            accessorKey: "createdAt",
            header: () => t("properties.columns.createdAt"),
            cell: ({ row }) => {
                const date = row.getValue("createdAt") as string;
                if (!date) return "-";

                try {
                    return format(new Date(date), "MMM d, yyyy");
                } catch (error) {
                    console.error('Error formatting date:', error);
                    return date;
                }
            },
        },
        {
            id: "actions",
            header: () => t("properties.columns.actions"),
            cell: ({ row }) => <PropertyActions property={row.original} />,
        },
    ];
};

const PropertyLink = ({ property, children }: { property: PropertyResponseDto; children: React.ReactNode }) => (
    <Link
        to={`/properties/${property.propertyUUID}`}
        className="hover:bg-muted/50 rounded-md p-1 transition-colors block"
    >
        {children}
    </Link>
); 